import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import type { ElementRef } from '@angular/core';
import { Component, HostListener, Inject, Input, NgZone, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { IterableService } from '@core-mkt/services/iterable/iterable.service';
import { PhoneValidationService } from '@core-mkt/services/phone-validation/phone-validation.service';
import { getParsedLinkString, getQueryParamString } from '@core-mkt/services/query-param/query-helpers';
import { QueryParamService } from '@core-mkt/services/query-param/query-param.service';
import { VerticalService } from '@core-mkt/services/vertical/vertical.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { FormPayload } from '../../email-form/email-form/email-form';
import type { ExitIntentModal } from './exit-intent-modal';

@Component({
  selector: 'exit-intent-modal',
  templateUrl: './exit-intent-modal.component.html',
  styleUrls: ['./exit-intent-modal.component.scss'],
  standalone: true,
  imports: [CdkTrapFocus, CommonModule, ReactiveFormsModule],
})
export class ExitIntentModalComponent implements OnDestroy, OnInit {
  @Input() config: ExitIntentModal = {};

  showPopup = false;
  private exitIntentTriggered = false;
  private showDelayTimeout?: number;
  private triggerTimerTimeout?: number;
  private isDestroyed = false;
  private lastScrollTop = 0;
  /** Element that had focus before the popup opened, so closing can hand focus back to it. */
  private previouslyFocused?: HTMLElement;
  isCaptureInfoInvalid = false;
  emailForm: FormGroup;
  postErr = false;
  formSubmitted = false;
  readonly secondaryButtonText = 'Maybe Later';
  readonly sensitivity = 5; // Sensitivity in pixels from top
  readonly showDelay = 500; // Delay in milliseconds before showing the popup
  readonly sensitiveScrolling = 20; // Sensitivity in pixels for scrolling detection
  readonly timerTriggerDelay = 7000; // Time on page in milliseconds before the timer trigger shows the popup
  titleHtml: string;
  contentHtml: string;
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly phoneValidation = inject(PhoneValidationService);
  private readonly vertical = inject(VerticalService);
  private readonly redactor = inject(WysiwygRedactorService);

  /**
   * Iterable's `type` is a vertical, not a brand id. Every brand that has no
   * configured `iterable.appType` (AARP today) is a driving brand, so DE is the
   * safe default rather than leaking a brand id downstream.
   */
  private readonly defaultVertical = 'DE';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private accessibilityService: AccessibilityService,
    private readonly formBuilder: FormBuilder,
    private env: EnvService,
    private iterable: IterableService,
    private queryParamService: QueryParamService,
    private brand: BrandConfigurationService,
  ) {
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  /** EPS-4992: page URL sent to Iterable — query string and fragment dropped so UTMs/coupons/tokens never leave. */
  private get siteUrl(): string | undefined {
    const location = this.document.defaultView?.location;
    return location ? `${location.origin}${location.pathname}` : undefined;
  }

  get title(): string {
    return this.config.titleModal || "Wait! Don't leave yet!";
  }

  get content(): string {
    return this.config.contentModal || 'Complete your purchase and save 20% today!';
  }

  get primaryButtonText(): string {
    return this.config.buttonLabelModal || 'Get My Discount';
  }

  get primaryButtonStyle(): string {
    return this.config.buttonStyleModal + '-button' || 'primary-button';
  }

  get primaryButtonLink(): string {
    return this.config.buttonLinkModal || '/';
  }

  get imagePlacement(): string {
    return this.config.imagePlacement || 'left';
  }

  get isDisabled(): boolean {
    return !this.config.enabledExitModal;
  }

  /**
   * Only an explicit `timer` opts out of exit intent. Unset, null, `exitIntent`, and any value
   * Craft adds later all keep the mouse-leave / scroll-up behavior.
   */
  get isTimerTrigger(): boolean {
    return this.config.triggerTypeModal === 'timer';
  }

  get imageUrl(): string {
    return this.config.imageModal?.[0]?.url || '';
  }

  get flexClass(): string {
    return this.config.imagePlacement === 'right' ? 'flex-row-reverse' : 'flex-row';
  }

  get iterableListId(): number {
    return this.config.iterableListId || 0;
  }

  /**
   * Vertical sent to Iterable as `type` — DE, RE, INS or MTG. Sourced from the
   * brand config so it stays in sync with the `appType` the IterableService
   * stamps onto `dataFields`.
   */
  get iterableVertical(): string {
    return this.vertical.getVerticalInfo().appType || this.defaultVertical;
  }

  /**
   * Labels the scrollable content region. The region is focusable so a keyboard
   * user can scroll it directly (WCAG 2.1.1) — without a label the extra tab
   * stop is announced as an unnamed region.
   */
  get scrollRegionLabel(): string {
    return 'Modal content';
  }

  /** Id of the title element that gives the dialog its accessible name. */
  readonly titleId = 'exit-intent-modal-title';

  /**
   * Moves focus into the dialog as soon as it renders. The query only resolves while
   * `showPopup` is true, so this runs once per open regardless of which trigger fired.
   */
  @ViewChild('dialog')
  set dialogElement(dialog: ElementRef<HTMLElement> | undefined) {
    dialog?.nativeElement.focus();
  }

  get postSubmissionMessage(): string {
    return this.config.postSubmissionMessage || '';
  }

  get placeholderInput(): string {
    return this.config.bottomSectionModal === 'email' ? 'youremail@domain.com' : '+1 (512) 555-0123';
  }

  /** Raw email/phone control value, narrowed off `AbstractControl.value`'s `any`. */
  private get capturedValue(): string {
    const value: unknown = this.emailForm.get('email')?.value;
    return typeof value === 'string' ? value : '';
  }

  get captureInfoError(): string {
    return this.config.bottomSectionModal === 'email'
      ? 'Please enter a valid email'
      : 'Please enter a valid phone number';
  }

  ngOnInit(): void {
    this.config ??= {};
    this.titleHtml = this.redactor.bypassSanitizer(this.title);
    this.contentHtml = this.redactor.bypassSanitizer(this.content);
    this.startTimerTrigger();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.showDelayTimeout && typeof window !== 'undefined') {
      window.clearTimeout(this.showDelayTimeout);
    }
    if (this.triggerTimerTimeout && typeof window !== 'undefined') {
      window.clearTimeout(this.triggerTimerTimeout);
    }
    // closePopup never runs on in-app navigation, so release the lock here or the next page can't scroll.
    if (this.showPopup) {
      this.document.body.style.overflow = '';
    }
  }

  /**
   * Browser-only, and scheduled outside the Angular zone: on the server a pending 7s macrotask
   * would hold the SSR response open, and in the browser it would keep the app unstable (delaying
   * hydration-dependent work) for the whole wait. Re-enters the zone only to show the popup.
   * Armed once at init because `[config]` is set once from the route snapshot; if config ever
   * becomes reactive, clear and re-arm this from ngOnChanges.
   */
  private startTimerTrigger(): void {
    if (!this.isTimerTrigger || this.isDisabled || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.triggerTimerTimeout = window.setTimeout(() => {
        this.ngZone.run(() => this.triggerExitIntent(0));
      }, this.timerTriggerDelay);
    });
  }

  private isMobileDevice(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return false;
    }
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (window.innerWidth <= 768 && 'ontouchstart' in window)
    );
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (this.isTimerTrigger || !this.isMobileDevice()) {
      return;
    }
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    if (currentScroll + this.sensitiveScrolling < this.lastScrollTop) {
      this.triggerExitIntent();
    }
    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // To avoid negative scrolling values
  }

  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent): void {
    if (this.isTimerTrigger || this.isDisabled || this.exitIntentTriggered || this.showPopup) {
      return;
    }

    // Check if mouse is leaving through the top of the viewport
    if (event.clientY <= this.sensitivity) {
      this.triggerExitIntent();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Close popup on Escape key
    if (event.key === 'Escape' && this.showPopup) {
      this.closePopup();
    }
  }

  /**
   * @param delay ms before showing. Exit-intent signals use `showDelay`; the timer trigger passes 0
   * because its own wait already elapsed.
   */
  private triggerExitIntent(delay: number = this.showDelay): void {
    const isClosedBefore =
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(`closedExitPopUp-${this.document.location.pathname}`) === 'true'
        : false;
    if (this.isDisabled || this.exitIntentTriggered || this.showPopup || this.isDestroyed || isClosedBefore) {
      return;
    }

    this.exitIntentTriggered = true;

    if (delay > 0) {
      if (typeof window !== 'undefined') {
        this.showDelayTimeout = window.setTimeout(() => {
          this.displayPopup();
        }, delay);
      } else {
        this.displayPopup();
      }
    } else {
      this.displayPopup();
    }
  }

  private displayPopup(): void {
    if (this.isDestroyed) {
      return;
    }

    const active = this.document.activeElement;
    this.previouslyFocused = active instanceof HTMLElement ? active : undefined;
    this.showPopup = true;

    // Prevent body scrolling when popup is open
    this.document.body.style.overflow = 'hidden';
  }

  closePopup(): void {
    this.showPopup = false;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(`closedExitPopUp-${this.document.location.pathname}`, 'true');
    }
    this.document.body.style.overflow = '';
    this.previouslyFocused?.focus();
    this.previouslyFocused = undefined;
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  getTargetUrl(): string | undefined {
    const link = this.config.buttonLinkModal;
    if (!link) {
      return undefined;
    }
    const queryParams = this.queryParamService.getQueryParams();
    const blogPrefix = this.env.get.blogPrefix;
    const queryString = getQueryParamString(queryParams, link, blogPrefix);
    return getParsedLinkString(link, queryString, this.brand.hostName, this.brand.siblingSites, blogPrefix);
  }

  onPrimaryAction(): void {
    const target = this.getTargetUrl();
    if (target) {
      window.location.href = target;
    }
    this.closePopup();
  }

  async submit(): Promise<void> {
    this.postErr = false;
    const emailRegEx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/g;
    let emailValue = this.capturedValue;
    if (this.config.bottomSectionModal === 'email') {
      if (!emailRegEx.test(this.capturedValue)) {
        this.isCaptureInfoInvalid = true;
        return;
      }
    } else {
      const e164 = this.phoneValidation.normalizeToE164(this.capturedValue);
      if (!e164) {
        this.isCaptureInfoInvalid = true;
        return;
      }

      const e164Digits = e164.replace('+', '');
      emailValue = `${e164Digits}@placeholder.email`;
    }

    this.isCaptureInfoInvalid = false;
    const iterableData: FormPayload = {
      listId: this.iterableListId,
      subscribers: [
        {
          dataFields: {
            brandId: this.env.get.brandConfig.id,
            siteUrl: this.siteUrl,
          },
          email: emailValue,
        },
      ],
      type: this.iterableVertical,
    };

    await this.iterable
      .addToMailList(iterableData, this.env.get.brandConfig.id)
      .catch(() => {
        this.postErr = true;
        // eslint-disable-next-line no-console
        console.error('Error subscribing user to Iterable list.');
      })
      .then(() => {
        if (!this.postErr) {
          this.formSubmitted = true;
        }
      });
  }
}
